/**
 * ManualTransferStep — Manual Bank Transfer flow inside the checkout modal.
 *
 * Two phases inside a single step for cohesion:
 *   DETAILS  — VT bank/account/amount with copy affordances + "I've paid"
 *   WAITING  — poll GET /checkout/manual-transfer/status/:orderId every 5s
 *              matched         → transition to ORDER_CONFIRMED
 *              amount_mismatch → show "we received ₦X but your order is
 *                                ₦Y, contact merchant"
 *              expired         → show "we didn't receive it yet" fallback
 *              awaiting        → keep polling
 *
 * Backend calls (both public — no auth needed):
 *   POST /api/checkout/manual-transfer/initiate  { orderId }
 *   GET  /api/checkout/manual-transfer/status/:orderId
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch as useReduxDispatch } from 'react-redux';
import { clearCart } from '../../../features/cart/cartSlice';
import { STEPS } from '../CheckoutModal';
import { apiFetch } from '../../../utils/api';

const POLL_INTERVAL_MS = 5000;

const formatMoney = (amount, currency = 'NGN') => {
  try {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(Number(amount) || 0);
  } catch {
    return `₦${(Number(amount) || 0).toFixed(2)}`;
  }
};

const CopyButton = ({ value, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop — clipboard blocked (older browser / iframe) */
    }
  };
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  return (
    <button type="button" className="cm-mt-copy" onClick={onClick} aria-label={`Copy ${label.toLowerCase()}`}>
      {copied ? '✓ Copied' : label}
    </button>
  );
};

const ManualTransferStep = ({ state, dispatch, goToStep, storeData, onClose }) => {
  const reduxDispatch = useReduxDispatch();
  const mt = state.manualTransfer || {};
  const orderId = mt.orderId;
  const amount = Number(mt.amount) || 0;
  const currency = mt.currency || 'NGN';
  const vt = mt.virtualTerminal || {};

  const [phase, setPhase] = useState('DETAILS'); // 'DETAILS' | 'WAITING'
  const [pollStatus, setPollStatus] = useState('awaiting'); // awaiting | matched | amount_mismatch | expired
  const [pollDetails, setPollDetails] = useState(null); // { received, expected } for mismatch
  const [initiateError, setInitiateError] = useState(null);
  const [initiating, setInitiating] = useState(false);
  // The transfer window is armed when terminal details are revealed.
  // “I've sent the money” only switches to confirmation/polling.
  const [expiresAt, setExpiresAt] = useState(() => {
    const value = mt.expiresAt ? new Date(mt.expiresAt) : null;
    return value && !Number.isNaN(value.getTime()) ? value : null;
  }); // Date
  const [remainingSec, setRemainingSec] = useState(null); // integer
  const pollTimerRef = useRef(null);
  const tickTimerRef = useRef(null);

  // Cleanup poll timer whenever we leave the WAITING phase or unmount.
  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const stopCountdown = useCallback(() => {
    if (tickTimerRef.current) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => { stopPolling(); stopCountdown(); }, [stopPolling, stopCountdown]);

  // Keep the transfer window visible in both details and confirmation.
  useEffect(() => {
    if (!expiresAt || pollStatus !== 'awaiting') {
      stopCountdown();
      return undefined;
    }
    const tick = () => {
      const ms = expiresAt.getTime() - Date.now();
      setRemainingSec(Math.max(0, Math.ceil(ms / 1000)));
    };
    tick();
    tickTimerRef.current = setInterval(tick, 1000);
    return stopCountdown;
  }, [expiresAt, pollStatus, stopCountdown]);

  const pollOnce = useCallback(async () => {
    if (!orderId) return;
    try {
      const resp = await apiFetch(`/checkout/manual-transfer/status/${orderId}`);
      const data = await resp.json();
      if (!data?.success) {
        pollTimerRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
        return;
      }

      if (data.status === 'matched') {
        stopPolling();
        setPollStatus('matched');
        reduxDispatch(clearCart());
        dispatch({
          type: 'SET_FIELDS',
          fields: {
            completedOrder: { id: orderId, orderNumber: data.order?.orderNumber || mt.orderNumber },
          },
        });
        goToStep(STEPS.ORDER_CONFIRMED);
        return;
      }

      if (data.status === 'amount_mismatch') {
        stopPolling();
        setPollStatus('amount_mismatch');
        setPollDetails({ received: data.received, expected: data.expected, currency: data.currency });
        return;
      }

      if (data.status === 'expired') {
        stopPolling();
        setPollStatus('expired');
        return;
      }

      // awaiting → keep polling
      pollTimerRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
    } catch {
      // Transient network fault — keep trying, don't scare the customer
      pollTimerRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
    }
  }, [orderId, stopPolling, reduxDispatch, dispatch, goToStep, mt.orderNumber]);

  const handleIvePaid = async () => {
    if (!orderId || initiating) return;
    setInitiating(true);
    setInitiateError(null);
    try {
      const resp = await apiFetch('/checkout/manual-transfer/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId }),
      });
      const data = await resp.json();
      if (!resp.ok || !data?.success) {
        setInitiateError(data?.error || 'Could not confirm your transfer. Please try again.');
        setInitiating(false);
        return;
      }
      if (data.alreadyPaid) {
        // Rare edge case — order was already paid before initiate landed.
        reduxDispatch(clearCart());
        dispatch({
          type: 'SET_FIELDS',
          fields: { completedOrder: { id: orderId, orderNumber: mt.orderNumber } },
        });
        goToStep(STEPS.ORDER_CONFIRMED);
        return;
      }
      // New backends return the original order window. Older ones may omit
      // it, in which case keep the value armed when details were shown.
      const returnedExpiresAt = data?.order?.expiresAt
        ? new Date(data.order.expiresAt)
        : expiresAt;
      if (returnedExpiresAt && !Number.isNaN(returnedExpiresAt.getTime())) {
        setExpiresAt(returnedExpiresAt);
      }
      setPhase('WAITING');
      // Kick off the poll loop immediately.
      pollOnce();
    } catch (err) {
      setInitiateError(err.message || 'Network error — please retry.');
      setInitiating(false);
    }
  };

  // No manual-transfer context — shouldn't happen but guard against a
  // direct navigation to this step without state.
  if (!orderId) {
    return (
      <div className="cm-step-content">
        <p className="cm-error">Manual transfer session lost. Please restart checkout.</p>
        <div className="cm-step-footer">
          <button className="cm-btn-primary cm-btn-full" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  // ─── DETAILS phase ────────────────────────────────────────────────
  if (phase === 'DETAILS') {
    return (
      <div className="cm-step-content cm-mt-step">
        <div className="cm-mt-intro">
          <div className="cm-mt-eyebrow">
            <span className="cm-mt-security-icon" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none">
                <rect x="3.25" y="7" width="9.5" height="6.25" rx="1.25" />
                <path d="M5.5 7V5.25a2.5 2.5 0 0 1 5 0V7" />
              </svg>
            </span>
            <span>Secure bank transfer</span>
          </div>
          <h3 className="cm-mt-title">Transfer to complete your order</h3>
          <p className="cm-mt-sub">
            Send the <strong>exact amount</strong> to the account below. We match it
            to your order automatically — usually within a minute.
          </p>
        </div>

        <div className="cm-mt-amount">
          <span className="cm-mt-amount-label">Amount to transfer</span>
          <div className="cm-mt-amount-row">
            <span className="cm-mt-amount-value">{formatMoney(amount, currency)}</span>
            <CopyButton value={amount.toFixed(2)} label="Copy amount" />
          </div>
        </div>

        <div className="cm-mt-account">
          <div className="cm-mt-account-heading">
            <span>Transfer details</span>
            <span>Use exactly as shown</span>
          </div>
          <div className="cm-mt-row">
            <span className="cm-mt-row-label">Bank</span>
            <span className="cm-mt-row-value">{vt.bank || 'Paystack-Titan'}</span>
          </div>
          <div className="cm-mt-row">
            <span className="cm-mt-row-label">Account number</span>
            <span className="cm-mt-row-value cm-mt-row-mono">
              {vt.accountNumber || '—'}
              {vt.accountNumber && <CopyButton value={vt.accountNumber} />}
            </span>
          </div>
          <div className="cm-mt-row">
            <span className="cm-mt-row-label">Account name</span>
            <span className="cm-mt-row-value">{vt.accountName || storeData?.name || '—'}</span>
          </div>
        </div>

        <p className="cm-mt-note">
          Transferring a different amount may delay confirmation — please match
          it to the last kobo.
        </p>

        {remainingSec != null && remainingSec > 0 && (
          <div className="cm-mt-countdown" aria-live="polite">
            <span className="cm-mt-countdown-label">Complete your transfer within</span>
            <span className="cm-mt-countdown-value">{`${Math.floor(remainingSec / 60)}:${String(remainingSec % 60).padStart(2, '0')}`}</span>
          </div>
        )}

        {initiateError && <div className="cm-error">{initiateError}</div>}

        <div className="cm-step-footer">
          <button
            className="cm-btn-primary cm-btn-full cm-mt-confirm"
            onClick={handleIvePaid}
            disabled={initiating}
            type="button"
          >
            <span>{initiating ? 'Confirming…' : "I've sent the money"}</span>
            {!initiating && <span className="cm-mt-confirm-arrow" aria-hidden="true">→</span>}
          </button>
        </div>
      </div>
    );
  }

  // ─── WAITING phase ────────────────────────────────────────────────
  if (pollStatus === 'amount_mismatch') {
    return (
      <div className="cm-step-content cm-mt-step cm-mt-mismatch">
        <div className="cm-mt-status-icon" aria-hidden="true">⚠️</div>
        <h3 className="cm-mt-title">Amount doesn't match</h3>
        <p className="cm-mt-sub">
          We received a transfer of <strong>{formatMoney(pollDetails?.received, pollDetails?.currency || currency)}</strong>
          {' '}but your order is for <strong>{formatMoney(pollDetails?.expected || amount, currency)}</strong>.
          The store has been notified — please contact them to resolve.
        </p>
        <div className="cm-step-footer">
          <button className="cm-btn-primary cm-btn-full" onClick={onClose} type="button">
            Close
          </button>
        </div>
      </div>
    );
  }

  if (pollStatus === 'expired') {
    return (
      <div className="cm-step-content cm-mt-step cm-mt-expired">
        <div className="cm-mt-status-icon" aria-hidden="true">⏳</div>
        <h3 className="cm-mt-title">Still waiting for your transfer</h3>
        <p className="cm-mt-sub">
          We haven't received your transfer yet. Please check your bank app, or
          contact the store if you've already sent it — they can confirm manually.
        </p>
        <div className="cm-step-footer">
          <button
            className="cm-btn-secondary"
            onClick={() => { setPollStatus('awaiting'); pollOnce(); }}
            type="button"
          >
            Check again
          </button>
          <button className="cm-btn-primary cm-btn-full" onClick={onClose} type="button">
            Close
          </button>
        </div>
      </div>
    );
  }

  // Format M:SS from the remaining seconds (nullable → em-dash placeholder).
  const countdownLabel = remainingSec == null
    ? '—:—'
    : `${Math.floor(remainingSec / 60)}:${String(remainingSec % 60).padStart(2, '0')}`;

  return (
    <div className="cm-step-content cm-mt-step cm-mt-waiting">
      <div className="cm-mt-spinner" aria-hidden="true">
        <svg width="42" height="42" viewBox="0 0 42 42" fill="none">
          <circle cx="21" cy="21" r="18" stroke="#e5e7eb" strokeWidth="4" />
          <path d="M21 3a18 18 0 0118 18" stroke="#0d7463" strokeWidth="4" strokeLinecap="round">
            <animateTransform attributeName="transform" type="rotate" from="0 21 21" to="360 21 21" dur="0.9s" repeatCount="indefinite" />
          </path>
        </svg>
      </div>
      <h3 className="cm-mt-title">Confirming your payment…</h3>
      <p className="cm-mt-sub">
        This usually takes under a minute. Please don't close this window.
      </p>
      {remainingSec != null && remainingSec > 0 && (
        <div className="cm-mt-countdown" aria-live="polite">
          <span className="cm-mt-countdown-label">We'll keep checking for</span>
          <span className="cm-mt-countdown-value">{countdownLabel}</span>
        </div>
      )}
    </div>
  );
};

export default ManualTransferStep;
